import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { FeatureFlag } from '@lastday/shared';
import { useMe, useTelegramLogin } from '@/api/hooks';
import { AppShell } from '@/components/AppShell';
import { Toaster } from '@/components/Toast';
import { EmptyState, ErrorState, Loading } from '@/components/ui';
import { t } from '@/i18n';
import { useRealtime } from '@/realtime/socket';
import { registerServiceWorker } from '@/lib/push';
import { useDeadlines } from '@/lib/live';
import { NewHomeCard } from '@/components/MoveCards';

const Landing = lazy(() => import('@/screens/landing/LandingScreen'));
const Onboarding = lazy(() => import('@/screens/onboarding/OnboardingScreen'));
const Camp = lazy(() => import('@/screens/camp/CampScreen'));
const MapScreen = lazy(() => import('@/screens/map/MapScreen'));
const Prepare = lazy(() => import('@/screens/prepare/PrepareScreen'));
const Report = lazy(() => import('@/screens/report/ReportScreen'));
const Revenge = lazy(() => import('@/screens/report/RevengeScreen'));
const Survivor = lazy(() => import('@/screens/survivor/SurvivorScreen'));
const Appearance = lazy(() => import('@/screens/survivor/AppearanceScreen'));
// 4. 10. 2026 (package 3): the Veteran Ledger and the flow after a season's end.
const Ledger = lazy(() => import('@/screens/survivor/LedgerScreen'));
const SeasonEndFlow = lazy(() => import('@/screens/season/SeasonEndFlow'));
const PickLook = lazy(() => import('@/screens/onboarding/PickLookScreen'));
const SessionEnd = lazy(() => import('@/screens/session/SessionEndScreen'));
const Skills = lazy(() => import('@/screens/skills/SkillsScreen'));
const Inventory = lazy(() => import('@/screens/inventory/InventoryScreen'));
const Workshop = lazy(() => import('@/screens/workshop/WorkshopScreen'));
const Feed = lazy(() => import('@/screens/feed/FeedScreen'));
const Horde = lazy(() => import('@/screens/horde/HordeScreen'));
const Collection = lazy(() => import('@/screens/collection/CollectionScreen'));
const History = lazy(() => import('@/screens/history/HistoryScreen'));
const EncounterOverlay = lazy(() => import('@/screens/encounter/EncounterOverlay'));
const EventOverlay = lazy(() => import('@/screens/events/EventOverlay'));
const ReportAutoOpen = lazy(() => import('@/screens/report/ReportAutoOpen'));
const EncounterScreen = lazy(() => import('@/screens/encounter/EncounterScreen'));
const Clan = lazy(() => import('@/screens/clan/ClanScreen'));
const Story = lazy(() => import('@/screens/story/StoryScreen'));
const Friends = lazy(() => import('@/screens/social/FriendsScreen'));
const Notifications = lazy(() => import('@/screens/social/NotificationsScreen'));
const Settings = lazy(() => import('@/screens/social/SettingsScreen'));
const Privacy = lazy(() => import('@/screens/legal/PrivacyScreen'));
// 12. 10. 2026 (owner, BACKLOG #52): the admin dashboard; linked from nowhere, its own chunk.
const Admin = lazy(() => import('@/screens/admin/AdminScreen'));
const Trader = lazy(() => import('@/screens/trader/TraderScreen'));
// 11. 10. 2026 (owner): the Market, behind its flag.
const Market = lazy(() => import('@/screens/market/MarketScreen'));
const Season = lazy(() => import('@/screens/season/SeasonScreen'));
const Guide = lazy(() => import('@/screens/guide/GuideScreen'));
// 11. 10. 2026 (owner, docs/thepit.md): The Pit, behind FEATURE_PIT.
const Pit = lazy(() => import('@/screens/pit/PitScreen'));
// 20. 10. 2026 (owner): the Survivors list and the public profile.
const Players = lazy(() => import('@/screens/players/PlayersScreen'));
const Profile = lazy(() => import('@/screens/players/ProfileScreen'));

declare global {
  interface Window {
    Telegram?: { WebApp?: { initData?: string; ready?: () => void; expand?: () => void } };
  }
}

/** Screens behind sign-in and survivor creation, inside the app shell. */
/** 11. 10. 2026 (BACKLOG #33): `/clan…` (old notifications and invite links) goes to `/crew…` with its query. */
function ToCrew() {
  const { tab } = useParams();
  return <Navigate to={`/crew${tab ? `/${tab}` : ''}${window.location.search}`} replace />;
}

function Game({ children, flag }: { children: ReactNode; flag?: FeatureFlag }) {
  const me = useMe();
  if (me.isLoading) return <Loading />;
  if (me.isError) return <ErrorState onRetry={() => me.refetch()} />;
  if (!me.data?.user) return <Navigate to="/" replace />;
  if (!me.data.survivor) return <Navigate to="/start" replace />;
  // Survivors from before section 8 pick their look once, before anything else.
  if (!me.data.survivor.bodyType) return <PickLook name={me.data.survivor.name} />;
  // 4. 10. 2026 (package 3, D): after a season's end the survivor walks out first (the end screen and its choices).
  if (me.data.survivor.seasonEnd && !me.data.survivor.seasonEnd.walkedOut && window.location.pathname !== '/season-end') return <Navigate to="/season-end" replace />;
  // A system outside the MVP that is switched off (krug 2, G).
  if (flag && me.data.features[flag] === false)
    return (
      <AppShell>
        <EmptyState title={t('common.featureOff')} body={t('common.featureOffBody')} />
      </AppShell>
    );
  return <AppShell>{children}</AppShell>;
}

function Home() {
  const me = useMe();
  if (me.isLoading) return <Loading />;
  const s = me.data?.survivor;
  if (s) return <Navigate to={s.pendingReportIds[0] ? `/report/${s.pendingReportIds[0]}` : '/camp'} replace />;
  return <Landing />;
}

function Referral() {
  const { code } = useParams();
  useEffect(() => {
    try {
      if (code) localStorage.setItem('ld_ref', code.toUpperCase());
    } catch {
      /* private mode */
    }
  }, [code]);
  return <Navigate to="/" replace />;
}

function Boot({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useMe();
  const tg = useTelegramLogin();
  // 12. 10. 2026 (owner, BACKLOG #52, round 3): the admin page is apart from the game: no game overlays on it.
  const admin = useLocation().pathname.startsWith('/admin');
  useRealtime(qc, me.data?.user?.id);
  // B1: timers that reach 0 update the screen, and coming back to the game asks for fresh data.
  useDeadlines();
  useEffect(() => {
    registerServiceWorker();
  }, []);
  // Telegram Mini App: sign in with the launch data, no registration.
  useEffect(() => {
    const init = window.Telegram?.WebApp?.initData;
    if (init && me.data && !me.data.user && !tg.isPending && me.data.features.telegram) {
      window.Telegram?.WebApp?.ready?.();
      window.Telegram?.WebApp?.expand?.();
      tg.mutate({ initData: init });
    }
  }, [me.data, tg]);
  return (
    <>
      {children}
      {me.data?.survivor && !admin && (
        <Suspense fallback={null}>
          <EncounterOverlay />
          <EventOverlay />
          <ReportAutoOpen />
        </Suspense>
      )}
      {/* Package 4, part M: an old address: the new home covers the game. */}
      {!admin && <NewHomeCard />}
      <Toaster />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Boot>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/welcome" element={<Landing />} />
            <Route path="/start" element={<Onboarding />} />
            <Route path="/s/:code" element={<Story />} />
            <Route path="/r/:code" element={<Referral />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/camp" element={<Game><Camp /></Game>} />
            <Route path="/map" element={<Game><MapScreen /></Game>} />
            <Route path="/map/:zone" element={<Game><MapScreen /></Game>} />
            <Route path="/prepare/:zone" element={<Game><Prepare /></Game>} />
            <Route path="/report/:id" element={<Game><Report /></Game>} />
            <Route path="/revenge/:id" element={<Game><Revenge /></Game>} />
            <Route path="/sent/:id" element={<Game><SessionEnd /></Game>} />
            <Route path="/survivor" element={<Game><Survivor /></Game>} />
            <Route path="/survivor/appearance" element={<Game><Appearance /></Game>} />
            <Route path="/survivor/ledger" element={<Game><Ledger /></Game>} />
            <Route path="/season-end" element={<Game><SeasonEndFlow /></Game>} />
            <Route path="/skills" element={<Game><Skills /></Game>} />
            <Route path="/collection" element={<Game><Collection /></Game>} />
            <Route path="/history" element={<Game><History /></Game>} />
            <Route path="/inventory" element={<Game><Inventory /></Game>} />
            <Route path="/inventory/:id" element={<Game><Inventory /></Game>} />
            <Route path="/workshop" element={<Game><Workshop /></Game>} />
            <Route path="/trader" element={<Game><Trader /></Game>} />
            <Route path="/market" element={<Game flag="market"><Market /></Game>} />
            <Route path="/season" element={<Game><Season /></Game>} />
            <Route path="/feed" element={<Game><Feed /></Game>} />
            <Route path="/players" element={<Game><Players /></Game>} />
            <Route path="/players/:uid" element={<Game><Profile /></Game>} />
            <Route path="/horde" element={<Game flag="horde"><Horde /></Game>} />
            <Route path="/pit" element={<Game flag="pit"><Pit /></Game>} />
            <Route path="/encounter/:id" element={<Game><EncounterScreen /></Game>} />
            {/* 11. 10. 2026 (BACKLOG #33): the Crew tab; the old group links (notifications, invites) still work. */}
            <Route path="/crew" element={<Game flag="groups"><Clan /></Game>} />
            <Route path="/crew/:tab" element={<Game flag="groups"><Clan /></Game>} />
            <Route path="/clan" element={<ToCrew />} />
            <Route path="/clan/:tab" element={<ToCrew />} />
            <Route path="/friends" element={<Game flag="friends"><Friends /></Game>} />
            <Route path="/notifications" element={<Game><Notifications /></Game>} />
            <Route path="/settings" element={<Game><Settings /></Game>} />
            <Route path="/guide" element={<Game><Guide /></Game>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Boot>
      </Suspense>
    </BrowserRouter>
  );
}
